// The article layout: a masterclass that reads like a long-form piece rather
// than a stack of cards.
//
// Opted into per module with `layout: "article"`. Nothing here touches the
// default reader, so the other twelve masterclasses render exactly as before
// and this can be judged on one of them first.
//
// The rules, from Coleman:
//
//   chapters, graphics and videos      the three things that carry the piece
//   no quotes                          the pull-quote treatment goes; the
//                                      sentence survives as a lead paragraph
//   no chapter numbers                 no "01." in a title, no "Chapter 4"
//                                      eyebrow; the progress bar has it
//   a few cards, otherwise bare        a border is reserved for the things a
//                                      coach ACTS on: the checklist, the
//                                      diagnostic, and the research asides.
//                                      Everything else is type and space.
//   larger text                        15px body to 17px, 1.75 to 1.8
//   nice motion, smooth feel           each block rises in once on first
//                                      sight, and never again
//
// `articleBlock` returns `undefined` for anything it does not restyle, and the
// reader falls through to its normal renderer. So the graphics, the videos,
// the checklist, the diagnostic and the science asides are untouched here on
// purpose: those already look right, and listing them would be a second place
// to keep in sync.

import React, { useEffect, useRef, useState } from "react";
import { GOLD } from "../constants/theme";
import { renderInline } from "./inlineMarkdown";
import { asStep } from "./actionStep";
import { chapterDisplayTitle } from "./chapterTitle";
import AfsMockDashboard from "./AfsMockDashboard";

/* ── Type scale ───────────────────────────────────────────────────────────── */

export const ARTICLE_BODY = 17.5;
export const ARTICLE_LH = 1.8;

/**
 * Headlines in Fraunces, body in DM Sans.
 *
 * The headline carries all the personality and the body stays the face a
 * coach already reads comfortably, so nothing about READING changes, only the
 * voice of the thing being read. Fraunces has real character above about
 * 20px and gets slightly eccentric below it, so it is strictly a display
 * face here: chapter titles, section headings, list numerals and captions.
 */
export const DISPLAY = "'Fraunces', Georgia, 'Times New Roman', serif";

const FONT_ID = "mc-article-font";
const FONT_HREF = "https://fonts.googleapis.com/css2"
  + "?family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;1,9..144,400"
  + "&display=swap";

/**
 * Pull Fraunces in on mount rather than from dashboard.css.
 *
 * One masterclass uses it, so the whole dashboard should not carry it. Idempotent
 * and never removed: leaving the link in place means switching chapters or
 * reopening the reader does not refetch or re-flash. `display=swap` keeps the
 * text visible while it loads, which matters most on the iOS bundle where the
 * font is the one thing that is not local.
 */
export function useArticleFont(enabled) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;
    if (document.getElementById(FONT_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_ID;
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

/**
 * Body colour, which is NOT C.textMid.
 *
 * textMid is tuned for 13-15px labels and captions sitting on cards. At 17px
 * over a long read it is too washed to be comfortable: the eye works harder
 * on every line and the page feels faint rather than calm. Article body sits
 * much closer to the heading colour, and the hierarchy is carried by size and
 * space instead of by greying the text down.
 */
export const bodyColor = (dark) => (dark ? "rgba(245,245,245,0.80)" : "#2B2B2B");

/**
 * Gold, but legible in light mode.
 *
 * #E2DD9F is a pale gold built for dark surfaces. On white at 10-11px bold it
 * is close to invisible, which is a real accessibility problem and not a taste
 * one: the kicker, the list numerals and the aside labels all disappear. The
 * light value is the same hue taken down until it clears 4.5:1 on white, so
 * the accent still reads as the brand without the reader squinting at it.
 */
export const accent = (dark) => (dark ? GOLD : "#857C2E");

/* ── Motion ───────────────────────────────────────────────────────────────── */

/**
 * Rise-in on first sight, then stop watching.
 *
 * Deliberately one-shot: re-animating on every scroll past is the thing that
 * makes a page feel restless rather than smooth. The observer disconnects
 * after the first intersection, so scrolling back up costs nothing and the
 * text does not flicker.
 *
 * Starts visible when IntersectionObserver is missing or motion is reduced,
 * so the copy is never gated behind an effect that might not run.
 */
export function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const reduced = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [seen, setSeen] = useState(() => reduced || typeof IntersectionObserver === "undefined");

  useEffect(() => {
    if (seen || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);

  return (
    <div ref={ref} style={{
      opacity: seen ? 1 : 0,
      transform: seen ? "none" : "translateY(10px)",
      transition: reduced ? "none" : `opacity .5s ease ${delay}ms, transform .5s cubic-bezier(.22,.61,.36,1) ${delay}ms`,
      willChange: seen ? "auto" : "opacity, transform",
    }}>{children}</div>
  );
}

/* ── Blocks ───────────────────────────────────────────────────────────────── */

function Body({ md, C, dark }) {
  // Same block parsing as the default Paragraph (blank line splits, "- " makes
  // a list), at the article's larger size.
  const out = [];
  let para = [];
  let bullets = [];
  const flushPara = () => {
    if (!para.length) return;
    out.push(
      <p key={out.length} style={{
        fontSize: ARTICLE_BODY, lineHeight: ARTICLE_LH, color: bodyColor(dark),
        margin: "0 0 22px", letterSpacing: "-0.003em",
      }}>{renderInline(para.join(" "))}</p>
    );
    para = [];
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    out.push(
      <ul key={out.length} style={{ margin: "0 0 22px", paddingLeft: 20 }}>
        {bullets.map((t, i) => (
          <li key={i} style={{
            fontSize: ARTICLE_BODY, lineHeight: ARTICLE_LH, color: bodyColor(dark), marginBottom: 8,
          }}>{renderInline(t)}</li>
        ))}
      </ul>
    );
    bullets = [];
  };
  for (const raw of String(md).split("\n")) {
    const line = raw.trimEnd();
    if (line.trimStart().startsWith("- ")) { flushPara(); bullets.push(line.trimStart().slice(2)); }
    else if (line.trim() === "") { flushBullets(); flushPara(); }
    else { flushBullets(); para.push(line); }
  }
  flushBullets(); flushPara();
  return <>{out}</>;
}

/** A section break. No gold dots, no gradient rules: space and a hairline. */
function SectionHeading({ text, C, id }) {
  return (
    <h3 id={id} style={{
      fontFamily: DISPLAY, fontSize: 25, fontWeight: 700, color: C.text,
      letterSpacing: "-0.014em", lineHeight: 1.26, margin: "54px 0 18px",
      paddingBottom: 13, borderBottom: `1px solid ${C.border}`, scrollMarginTop: 80,
      fontVariationSettings: "'opsz' 48",
    }}>{text}</h3>
  );
}

/**
 * What used to be a pull quote.
 *
 * No glyph, no quote marks, no rule: a standout line in an article is just
 * bigger type with air around it. The words are unchanged, so nothing written
 * is lost by turning the treatment off.
 */
function Lead({ md, C }) {
  return (
    <p style={{
      fontSize: 19, lineHeight: 1.66, fontWeight: 500, color: C.text,
      letterSpacing: "-0.012em", margin: "30px 0 28px",
    }}>{renderInline(md)}</p>
  );
}

/**
 * An aside. A hairline on the left and a quiet label, instead of a tinted
 * box with a shadowed paper tab stuck to its corner.
 */
function Aside({ tone = "tip", title, body, C, dark }) {
  const edge = tone === "warn" ? (dark ? "#F87171" : "#C2453F") : accent(dark);
  const label = tone === "warn" ? "Worth knowing" : tone === "quote" ? "Note" : "Tip";
  return (
    <aside style={{ margin: "30px 0", padding: "2px 0 2px 20px", borderLeft: `2px solid ${edge}` }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
        color: edge, marginBottom: 9,
      }}>{label}</div>
      {title && (
        <div style={{
          fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 7, letterSpacing: "-0.01em",
        }}>{title}</div>
      )}
      <div style={{ fontSize: 16, lineHeight: 1.72, color: bodyColor(dark) }}>{renderInline(body)}</div>
    </aside>
  );
}

/** A sequence, as a numbered list. Five boxes was the loudest thing on screen. */
function NumberedList({ items, C, dark, titleKey = "title", bodyKey = "body" }) {
  return (
    <ol style={{ listStyle: "none", margin: "28px 0", padding: 0 }}>
      {(items || []).map(asStep).map((it, i) => (
        <li key={i} style={{
          display: "grid", gridTemplateColumns: "34px 1fr", gap: 14,
          padding: "16px 0", borderTop: `1px solid ${C.border}`,
          ...(i === items.length - 1 ? { borderBottom: `1px solid ${C.border}` } : null),
        }}>
          <div style={{
            fontFamily: DISPLAY, fontSize: 21, fontWeight: 700, lineHeight: 1.35,
            color: accent(dark), opacity: dark ? 0.75 : 0.9,
            fontVariationSettings: "'opsz' 24",
          }}>{it.n ?? i + 1}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 17, fontWeight: it[bodyKey] ? 700 : 500, color: C.text,
              marginBottom: it[bodyKey] ? 4 : 0, letterSpacing: "-0.01em", lineHeight: 1.5,
            }}>{renderInline(it[titleKey] || "")}</div>
            {it[bodyKey] ? (
              <div style={{ fontSize: 16, lineHeight: 1.7, color: bodyColor(dark) }}>{renderInline(it[bodyKey])}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Two sides of one idea, adjacent and separated by a rule.
 *
 * Both column titles are the same colour on purpose: the old version made one
 * side gold, which read as "this is the good one" when the whole point of the
 * block is that both are goals.
 */
function TwoUp({ leftTitle, leftItems, rightTitle, rightItems, C, dark }) {
  const Col = ({ title, items, first }) => (
    <div style={{
      padding: first ? "20px 24px 22px 0" : "20px 0 22px 24px",
      borderLeft: first ? "none" : `1px solid ${C.border}`,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em",
        color: C.textDim, marginBottom: 14,
      }}>{title}</div>
      {items.map((t, i) => (
        <div key={i} style={{ fontSize: 15.5, lineHeight: 1.65, color: bodyColor(dark), padding: "6px 0" }}>
          {renderInline(t)}
        </div>
      ))}
    </div>
  );
  return (
    <div className="mc-article-twoup" style={{
      display: "grid", gridTemplateColumns: "1fr 1fr", margin: "32px 0",
      borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
    }}>
      <Col title={leftTitle} items={leftItems} first />
      <Col title={rightTitle} items={rightItems} />
    </div>
  );
}

/**
 * A little gallery of clips, side by side instead of stacked.
 *
 * The anchor clips are two takes of one idea, and stacked full-width they read
 * as two separate videos the reader scrolls past one at a time. Two-up they
 * read as what they are: a pair. Portrait by default (these are phone-shot
 * player clips) so each fills a tall 9:16 frame rather than letterboxing in a
 * wide one. Collapses to one column on a phone via ARTICLE_CSS.
 *
 * Self-contained Mux embed (poster behind the iframe, gold accent), the same
 * treatment as the reader's VideoBlock, kept here because only the article
 * uses the gallery.
 */
function VideoGallery({ items, portrait = true, C, dark }) {
  const clips = (items || []).filter(it => it && it.muxPlaybackId);
  if (!clips.length) return null;
  const aspect = portrait ? "9 / 16" : "16 / 9";
  const fit = portrait ? "cover" : "contain";
  return (
    <div className="mc-article-gallery" style={{
      display: "grid", gridTemplateColumns: `repeat(${Math.min(clips.length, 2)}, 1fr)`,
      gap: 14, margin: "22px 0",
    }}>
      {clips.map((it, i) => {
        const src = `https://player.mux.com/${it.muxPlaybackId}?accent-color=${encodeURIComponent(GOLD)}`;
        const poster = `https://image.mux.com/${it.muxPlaybackId}/thumbnail.jpg?width=640&fit_mode=preserve`;
        return (
          <div key={i}>
            <div style={{
              position: "relative", width: "100%", aspectRatio: aspect, background: "#000",
              borderRadius: 12, overflow: "hidden", isolation: "isolate", border: `1px solid ${C.cardBorder}`,
            }}>
              <img src={poster} alt="" aria-hidden="true" loading="lazy"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: fit, opacity: 0.55, borderRadius: 12 }} />
              <iframe
                title={it.caption || "Video"}
                src={src}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", borderRadius: 12 }} />
            </div>
            {it.caption && (
              <div style={{ fontSize: 12, color: C.textDim, marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>
                {renderInline(it.caption)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── The switch ───────────────────────────────────────────────────────────── */

/**
 * Article treatment for the blocks that needed it. `undefined` means "not mine",
 * and the reader falls through to its default renderer, which is how the
 * graphics, videos, checklist, diagnostic and science asides stay as they are.
 */
export function articleBlock(block, ctx) {
  const { C, dark, headingId } = ctx;
  switch (block.kind) {
    case "text":       return <Body md={block.md} C={C} dark={dark} />;
    case "pullquote":  return <Lead md={block.md} C={C} />;
    case "heading":    return block.level === 3
      ? <SectionHeading text={block.text} C={C} id={headingId} />
      : undefined;
    case "callout":    return <Aside tone={block.tone} title={block.title} body={block.body} C={C} dark={dark} />;
    case "principles": return <NumberedList items={block.items} C={C} dark={dark} />;
    case "actionSteps":return <NumberedList items={block.items} C={C} dark={dark} />;
    case "compare":    return <TwoUp {...block} C={C} dark={dark} />;
    // Embedded sample dashboard. Only the article uses it, and it is a leaf
    // (no block data beyond the team label), so it lives here rather than in
    // the default reader's renderBlock.
    case "afsDashboard": return <AfsMockDashboard C={C} dark={dark} team={block.team} />;
    case "videoGallery": return <VideoGallery items={block.items} portrait={block.portrait} C={C} dark={dark} />;
    default:           return undefined;
  }
}

export const ARTICLE_CSS = `
  .mc-article .mc-article-twoup { gap: 0; }
  @media (max-width: 620px) {
    .mc-article .mc-article-twoup { grid-template-columns: 1fr; }
    .mc-article .mc-article-twoup > div { padding: 18px 0 !important; border-left: none !important; }
    .mc-article .mc-article-twoup > div + div { border-top: 1px solid currentColor; opacity: 1; }
    /* A phone is too narrow for two portrait clips side by side: stack them,
       but cap the width so a single 9:16 clip is not a full-height wall. */
    .mc-article .mc-article-gallery { grid-template-columns: 1fr; justify-items: center; }
    .mc-article .mc-article-gallery > div { max-width: 300px; width: 100%; }
  }
`;

export default articleBlock;

/* ── Section dividers ─────────────────────────────────────────────────────── */

/**
 * The page list for a section, shown on its divider page.
 *
 * A divider carries a section name, whatever intro prose already belonged to
 * the section, and then this: what is coming. That makes it orientation rather
 * than a title card you page past, and it is the one place the structure is
 * visible without a sidebar.
 */
/**
 * The list of pages in a section, rendered on that section's divider page.
 *
 * `label` is for the opening page only. There the standfirst has taken the
 * headline slot, so the section's own name would otherwise go unsaid; it
 * reappears here, small, as the heading for the list it actually describes.
 */
export function SectionContents({ module, section, onGo, C, dark, label }) {
  const rows = module.chapters
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.section === section && !c.divider);
  if (!rows.length) return null;
  return (
    <nav style={{ margin: "34px 0 8px", borderTop: `1px solid ${C.border}` }}>
      {label && (
        <div style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: "0.17em",
          textTransform: "uppercase", color: accent(dark),
          padding: "18px 2px 2px",
        }}>{label}</div>
      )}
      {rows.map(({ c, i }, n) => (
        <button key={c.id} type="button" onClick={() => onGo(i)}
          style={{
            display: "flex", alignItems: "baseline", gap: 16, width: "100%",
            textAlign: "left", padding: "16px 2px", cursor: "pointer",
            background: "transparent", border: "none",
            borderBottom: `1px solid ${C.border}`, fontFamily: "inherit",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = dark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
          <span style={{
            fontFamily: DISPLAY, fontSize: 15, fontWeight: 700,
            color: accent(dark), opacity: dark ? 0.7 : 0.85, minWidth: 18,
          }}>{n + 1}</span>
          <span style={{
            fontSize: 17, fontWeight: 600, color: C.text, letterSpacing: "-0.012em",
            lineHeight: 1.4, flex: 1,
          }}>{chapterDisplayTitle(c.title)}</span>
        </button>
      ))}
    </nav>
  );
}
